import type { Course } from "@repo/app-types/entities/course";
import { isNotUndefined } from "@repo/app-types/helpers/utilities";
import { useCallback, useMemo, useState } from "react";
import type { Key } from "react-aria";
import { z } from "zod";

/** Describes the objects used to identify items in the course tree UI. */
const treeItemKeySchema = z.union([
  z.object({ type: z.literal("new-chapter") }),
  z.object({ type: z.literal("chapter"), id: z.string() }),
  z.object({ type: z.literal("new-lesson"), chapterId: z.string() }),
  z.object({ type: z.literal("lesson"), sectionId: z.string() }),
]);

/** Identifies items in the course tree UI. */
export type TreeItemKey = z.infer<typeof treeItemKeySchema>;

/** Turns a tree item key/identifier into a string for use with the `Tree` component. */
export function serializeTreeItemKey(key: TreeItemKey): Key & string {
  return JSON.stringify(key);
}

/** Parses a string into a tree item key/identifier. */
export function deserializeTreeItemKey(key: Key): TreeItemKey | undefined {
  if (typeof key !== "string") {
    return undefined;
  }

  const result = treeItemKeySchema.safeParse(JSON.parse(key));
  return result.success ? result.data : undefined;
}

/** Handles the expanding/collapsing of the course tree, expanding chapters by defalt. */
export function useCourseTreeExpandAndCollapse(
  course: Course = { chapters: [] },
) {
  const [collapsedChapterIds, setCollapsedChapterIds] = useState<
    readonly string[]
  >([]);

  const expandedKeys = useMemo(
    () =>
      course.chapters
        .filter((chapter) => !collapsedChapterIds.includes(chapter.id))
        .map((chapter) =>
          serializeTreeItemKey({ type: "chapter", id: chapter.id }),
        ),
    [course.chapters, collapsedChapterIds],
  );

  const onExpandedChange = useCallback(
    (keys: Set<Key>) => {
      const expandedChapterIds = Array.from(keys)
        .map(deserializeTreeItemKey)
        .map((key) => (key?.type === "chapter" ? key.id : undefined))
        .filter(isNotUndefined);

      const newCollapsedIds = course.chapters
        .map((chapter) => chapter.id)
        .filter((id) => !expandedChapterIds.includes(id));

      setCollapsedChapterIds(newCollapsedIds ?? []);
    },
    [course.chapters],
  );

  return { expandedKeys, onExpandedChange };
}
